/**
 * MedCom Send Besked Formular
 * 
 * Forenklet formular der viser alle felter tydeligt til certificerings-screenshots.
 * Genbruger eksisterende hooks og Edge Functions.
 */

import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Send, Loader2, Paperclip, Link, X } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';
import { useClinicUsersList } from '@/hooks/useClinicUsers';
import { useClinic } from '@/hooks/useClinicSettings';
import { useSorEntityName } from '@/hooks/useSorEntityName';
import { useForwardReceiverName } from '@/pages/communicate/useForwardReceiverName';
import { usePatientGivenFallback } from '@/pages/communicate/usePatientGivenFallback';
import { outgoingTopic, topicRequired } from '@/lib/medcom/composeTopic';
import {
  AUTHOR_FREE_TEXT,
  authorFromClinicUser,
  clinicAuthorizedSenders,
  composeClinicSender,
  preferredReplySender,
  replySenderChoices,
} from '@/lib/medcom/composeClinicSender';
import {
  CARE_COMMUNICATION_CATEGORIES,
  AUTHOR_ROLE_CODES,
  useSendCareCommunication,
  useClinicCommunicateConnection,
  useClinicSenderIdentities,
  useCareCommunicationThread,
  useBundleJson,
  useThreadLetterBundles,
  type AssembledThread,
  type CareCommunicationCategory,
  type CareCommunicationListItem,
  type SendCareCommunicationInput,
} from '@/hooks/useCareCommunications';
import {
  composeReceiver,
  listedPatientIdentifiers,
  patientIdentifiersFromField,
  replyLetterFields,
  replyPatientFields,
  replyPatientSource,
  type ReplyPatientIdentifiers,
  type ReplyCareTeam,
  type ReplyRecipient,
  specificRecipientBody,
} from '@/lib/medcom/replyPrefill';
import { replyParentMessageId } from '@/lib/medcom/replyParent';
import { ForwardPriorMessage } from '@/pages/communicate/PriorMessagePreview';
import { ThreadAttachmentPicker } from '@/pages/communicate/ThreadAttachmentPicker';
import { useForwardChoice } from '@/pages/communicate/useForwardChoice';
import {
  alreadyForwardedIdentifiers,
  attachmentTitleFromFileName,
  carriedAttachmentPayloads,
  mayCarryThreadAttachments,
  newAttachmentIdentifier,
  pickerThreadAttachments,
  threadAttachmentsFromLetters,
} from '@/lib/medcom/threadAttachments';
import { composeOutgoingPayloads } from '@/lib/medcom/newPayloads';
import { episodeOfCareLabel, replyEncounterPayload } from '@/lib/medcom/parseCareCommunicationBundle';
import { attachmentCreationTime } from '@/pages/communicate/attachmentCreation';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Skeleton } from '@/components/ui/Skeleton';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { ApiCallError } from '@/lib/api';
import { labShareCheck } from '@/lib/lab/labShareCheck';

/** MedCom ValueSet medcom-core-attachmentMimeTypes. Same six as the clinic compose form. */
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/bmp',
].join(',');

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

interface AttachmentItem {
  id: string;
  kind: 'file' | 'url';
  identifier: string;
  // file
  title?: string;
  contentType?: string;
  data?: string;
  creation?: string;
  // url
  url?: string;
}

export function MedComSendForm() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const action = searchParams.get('action') as 'new' | 'reply' | 'forward' | null;
  const threadId = searchParams.get('threadId');
  const messageId = searchParams.get('messageId');
  const labReportId = searchParams.get('labReportId');
  
  const { activeClinicId, activeClinic } = useActiveClinic();
  const { data: profile } = useCurrentUserProfile();
  const { data: clinicUsers } = useClinicUsersList(activeClinicId, { includeInactive: false });
  const { data: clinic, isFetched: clinicFetched } = useClinic(activeClinicId);
  const send = useSendCareCommunication();
  const { data: connection } = useClinicCommunicateConnection(activeClinicId);
  const { data: extraSenderRows } = useClinicSenderIdentities(activeClinicId);
  const replying = Boolean(threadId && (action === 'reply' || action === 'forward'));
  const { data: threadData } = useCareCommunicationThread(
    activeClinicId,
    replying ? threadId : undefined
  );
  const letterMessageId = messageId ?? (
    replying
      ? newestMessageId(threadData?.messages ?? [], action === 'reply')
      : null
  );
  const { data: letter } = useBundleJson(
    activeClinicId,
    replying && letterMessageId ? letterMessageId : undefined,
  );

  // Form state
  const [category, setCategory] = useState<CareCommunicationCategory>('other');
  const [topic, setTopic] = useState('');
  const [priority, setPriority] = useState<'routine' | 'asap'>('routine');
  const [messageText, setMessageText] = useState('');

  // Organization fields
  const [senderSorId, setSenderSorId] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderGln, setSenderGln] = useState('');
  const [receiverSorId, setReceiverSorId] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [receiverEan, setReceiverEan] = useState('');
  const [specificRecipientLabel, setSpecificRecipientLabel] = useState('');
  const [specificRecipient, setSpecificRecipient] = useState<ReplyRecipient | null>(null);
  const [specificRecipientCareTeam, setSpecificRecipientCareTeam] = useState<ReplyCareTeam | null>(
    null,
  );

  // Patient fields
  const [patientIdKind, setPatientIdKind] = useState<'cpr' | 'xEcpr' | 'dEcpr'>('cpr');
  const [patientCpr, setPatientCpr] = useState('');
  const [replyIdentifiers, setReplyIdentifiers] = useState<ReplyPatientIdentifiers>({});
  const [patientFamily, setPatientFamily] = useState('');
  const [patientGiven, setPatientGiven] = useState('');
  const [prefillRun, setPrefillRun] = useState(0);

  // Author fields
  const [authorFamily, setAuthorFamily] = useState('');
  const [authorGiven, setAuthorGiven] = useState('');
  const [authorRoleCode, setAuthorRoleCode] = useState('psykolog');
  const [authorPhone, setAuthorPhone] = useState('');
  const [authorUserId, setAuthorUserId] = useState('');
  const appliedAuthorUserId = useRef('');
  const senderPicked = useRef(false);

  // Attachments
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [selectedThreadAttachments, setSelectedThreadAttachments] = useState<Set<string>>(
    () => new Set(),
  );
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const messageType = action || 'new';
  const forwardChoice = useForwardChoice(letter, letterMessageId, messageType === 'forward');
  const loadThreadLetters = messageType === 'reply' || mayCarryThreadAttachments(messageType);
  const threadMessageIds = useMemo(
    () => (loadThreadLetters ? (threadData?.messages ?? []).map((item) => item.id) : []),
    [loadThreadLetters, threadData?.messages],
  );
  const { data: threadLetters } = useThreadLetterBundles(
    loadThreadLetters ? activeClinicId : null,
    threadMessageIds,
  );
  const replyParentId = useMemo(
    () =>
      messageType === 'reply'
        ? replyParentMessageId({
            openedId: letterMessageId,
            messages: threadData?.messages ?? [],
            letters: threadLetters ?? new Map(),
          })
        : null,
    [messageType, letterMessageId, threadData?.messages, threadLetters],
  );
  const threadAttachments = useMemo(
    () =>
      mayCarryThreadAttachments(messageType)
        ? threadAttachmentsFromLetters(threadData?.messages ?? [], threadLetters ?? new Map())
        : [],
    [messageType, threadData?.messages, threadLetters],
  );
  const forwardedIds = useMemo(
    () =>
      alreadyForwardedIdentifiers({
        mode: messageType,
        quotedAttachments: letter?.attachments ?? [],
        forwardSegmentIds: messageType === 'forward' ? forwardChoice.segmentIds : undefined,
      }),
    [messageType, letter?.attachments, forwardChoice.segmentIds],
  );
  const pickerAttachments = useMemo(
    () =>
      mayCarryThreadAttachments(messageType)
        ? pickerThreadAttachments(threadAttachments, forwardedIds)
        : [],
    [messageType, threadAttachments, forwardedIds],
  );
  const showPriority = category === 'regarding-referral';
  
  // Reset and pre-fill form when action or threadId changes
  useEffect(() => {
    setPrefillRun((run) => run + 1);
    // First, clear the form completely
    setMessageText('');
    setTopic('');
    setReceiverSorId('');
    setReceiverName('');
    setReceiverEan('');
    setPatientIdKind('cpr');
    setPatientCpr('');
    setReplyIdentifiers({});
    setPatientFamily('');
    setPatientGiven('');
    setAuthorFamily('');
    setAuthorGiven('');
    setAuthorPhone('');
    setAuthorUserId('');
    appliedAuthorUserId.current = '';
    senderPicked.current = false;
    setCategory('other');
    setPriority('routine');
    setAttachments([]);
    setUrlInput('');
    setSpecificRecipientLabel('');
    setSpecificRecipient(null);
    setSpecificRecipientCareTeam(null);

    if (action !== 'reply' && action !== 'forward') return;
    if (!threadData?.assembled) return;

    const assembled = threadData.assembled;
    const opened = letterMessageId
      ? threadData.messages.find((item) => item.id === letterMessageId)
      : undefined;

    const patient = replyPatientFields(
      replyPatientSource({
        letter: letter
          ? {
              cpr: letter.cpr,
              xEcpr: letter.xEcpr,
              dEcpr: letter.dEcpr,
              family: letter.patientFamily,
              given: letter.patientGiven,
              patientName: letter.patientName,
            }
          : null,
        assembled: assembled.patient,
      }),
    );
    if (patient) {
      setPatientIdKind(patient.idKind);
      setPatientCpr(patient.idValue);
      setPatientFamily(patient.family);
      setPatientGiven(patient.given);
      if (patient.idValue) setReplyIdentifiers(patient.identifiers);
    }

    const outgoing = opened?.direction === 'out';
    const fromLetter = letter
      ? replyLetterFields(letter, outgoing ? 'out' : 'in', senderSorId)
      : null;
    const categoryCode = fromLetter?.categoryCode ?? opened?.category ?? null;
    if (categoryCode && isCategory(categoryCode)) setCategory(categoryCode);
    const topicText = fromLetter?.topic ?? opened?.topic ?? '';
    if (fromLetter || opened) setTopic(topicText);

    if (action === 'reply') {
      const receiver = letter
        ? composeReceiver('reply', letter, outgoing ? 'out' : 'in', senderSorId)
        : null;
      const fromUs = Boolean(senderSorId && opened?.senderSor === senderSorId);
      const fallback = opened
        ? {
            sorId:
              outgoing || fromUs
                ? (opened.receiverSor ?? '')
                : (opened.senderSor ?? ''),
            name:
              outgoing || fromUs
                ? (opened.receiverOrganization ?? '')
                : (opened.senderOrganization ?? ''),
            ean: '',
          }
        : segmentSender(assembled);
      const sorId = receiver?.sorId || fallback.sorId;
      const name = receiver?.name || fallback.name;
      const gln = receiver?.ean || glnForSor(assembled, sorId);
      setReceiverSorId(sorId);
      if (name) setReceiverName(name);
      if (gln) setReceiverEan(gln);
      if (fromLetter) {
        setSpecificRecipientLabel(fromLetter.specificRecipientLabel);
        setSpecificRecipient(fromLetter.recipient);
        setSpecificRecipientCareTeam(fromLetter.recipientCareTeam);
      }
    }
  }, [threadData, action, threadId, letterMessageId, letter]);

  usePatientGivenFallback({
    enabled: action === 'reply' || action === 'forward',
    clinicId: activeClinicId,
    messageId: letterMessageId,
    family: patientFamily,
    given: patientGiven,
    setGiven: setPatientGiven,
    messages: threadData?.messages,
    letters: threadLetters,
    resetKey: prefillRun,
  });

  const receiverNameFromSor = useSorEntityName(
    action === 'reply' && !receiverName.trim() ? receiverSorId.trim() || null : null,
  );

  useEffect(() => {
    if (action !== 'reply' || receiverName.trim() || !receiverNameFromSor.name) return;
    setReceiverName(receiverNameFromSor.name);
  }, [action, receiverName, receiverNameFromSor.name]);

  useForwardReceiverName({
    enabled: messageType === 'forward',
    sorId: receiverSorId,
    name: receiverName,
    setName: setReceiverName,
    messages: threadData?.messages,
    letters: threadLetters,
    opened: letter,
  });

  const clinicSender = useMemo(
    () =>
      composeClinicSender({
        sorId: connection?.sor_id,
        name: activeClinic?.name,
        ean: connection?.ean,
      }),
    [connection?.sor_id, connection?.ean, activeClinic?.name],
  );
  const extraSenders = useMemo(
    () =>
      (extraSenderRows ?? []).map((row) => ({
        sorId: row.sor,
        name: row.name,
        ean: row.ean,
      })),
    [extraSenderRows],
  );
  const authorizedSenders = useMemo(
    () =>
      clinicAuthorizedSenders(
        {
          sorId: connection?.sor_id,
          name: activeClinic?.name,
          ean: connection?.ean,
        },
        extraSenders,
      ),
    [connection?.sor_id, connection?.ean, activeClinic?.name, extraSenders],
  );
  const openedMessage = letterMessageId
    ? threadData?.messages.find((item) => item.id === letterMessageId)
    : undefined;
  const letterDirection =
    openedMessage?.direction === 'out'
      ? 'out'
      : openedMessage?.direction === 'in'
        ? 'in'
        : null;
  const senderChoices = useMemo(
    () =>
      replySenderChoices({
        mode: messageType,
        direction: letterDirection,
        clinic: clinicSender,
        letterReceiver: {
          sorId: letter?.receiverSor ?? openedMessage?.receiverSor,
          name: letter?.receiverOrganization ?? openedMessage?.receiverOrganization,
          ean: letter?.receiverGln,
        },
        authorized: authorizedSenders,
      }),
    [
      messageType,
      letterDirection,
      clinicSender,
      letter?.receiverSor,
      letter?.receiverOrganization,
      letter?.receiverGln,
      openedMessage?.receiverSor,
      openedMessage?.receiverOrganization,
      authorizedSenders,
    ],
  );
  const preferredSender = useMemo(
    () =>
      preferredReplySender({
        mode: messageType,
        direction: letterDirection,
        clinic: clinicSender,
        letterReceiver: {
          sorId: letter?.receiverSor ?? openedMessage?.receiverSor,
          name: letter?.receiverOrganization ?? openedMessage?.receiverOrganization,
          ean: letter?.receiverGln,
        },
        authorized: authorizedSenders,
      }),
    [
      messageType,
      letterDirection,
      clinicSender,
      letter?.receiverSor,
      letter?.receiverOrganization,
      letter?.receiverGln,
      openedMessage?.receiverSor,
      openedMessage?.receiverOrganization,
      authorizedSenders,
    ],
  );
  const canChooseSender = senderChoices.length > 1;
  const authorLocked = authorUserId !== AUTHOR_FREE_TEXT;
  const authorOptions = useMemo(() => {
    const users = (clinicUsers ?? [])
      .map((member) => member.user)
      .filter((user): user is NonNullable<typeof user> => Boolean(user));
    if (profile && !users.some((user) => user.id === profile.id)) {
      return [profile, ...users];
    }
    return users;
  }, [clinicUsers, profile]);

  useEffect(() => {
    if (senderChoices.length === 0) return;
    const selected = senderPicked.current
      ? senderChoices.find((choice) => choice.sorId === senderSorId) ?? preferredSender
      : preferredSender;
    if (!selected?.sorId) return;
    setSenderSorId(selected.sorId);
    setSenderName(selected.name);
    setSenderGln(selected.gln);
  }, [senderChoices, preferredSender, senderSorId]);

  useEffect(() => {
    if (messageType !== 'reply' || !letter || !letterDirection) return;
    const fromLetter = replyLetterFields(letter, letterDirection, senderSorId);
    const receiver = composeReceiver('reply', letter, letterDirection, senderSorId);
    if (!receiver) return;
    if (receiver.sorId) setReceiverSorId(receiver.sorId);
    if (receiver.name) setReceiverName(receiver.name);
    if (receiver.ean) setReceiverEan(receiver.ean);
    setSpecificRecipientLabel(fromLetter.specificRecipientLabel);
    setSpecificRecipient(fromLetter.recipient);
    setSpecificRecipientCareTeam(fromLetter.recipientCareTeam);
  }, [messageType, letter, letterDirection, senderSorId]);

  useEffect(() => {
    if (!profile?.id) return;
    setAuthorUserId((current) => current || profile.id);
  }, [profile?.id]);

  useEffect(() => {
    if (!authorUserId || authorUserId === AUTHOR_FREE_TEXT) return;
    const source =
      authorUserId === profile?.id
        ? profile
        : clinicUsers?.find((member) => member.user?.id === authorUserId)?.user;
    if (!source) return;
    if (!source.phone && !clinicFetched) return;
    const author = authorFromClinicUser(source, clinic);
    if (appliedAuthorUserId.current !== authorUserId) {
      setAuthorFamily(author.family);
      setAuthorGiven(author.given);
      setAuthorPhone(author.phone);
      appliedAuthorUserId.current = authorUserId;
      return;
    }
    setAuthorPhone((phone) => phone || author.phone);
  }, [authorUserId, profile, clinicUsers, clinic, clinicFetched]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    const next: AttachmentItem[] = [];
    for (const file of files) {
      if (!ALLOWED_MIME_TYPES.split(',').includes(file.type)) {
        toast.error('Denne filtype er ikke tilladt. Tilladte: PDF, GIF, JPEG, PNG, TIFF, BMP.');
        continue;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`Filen "${file.name}" er for stor (${(file.size / 1024 / 1024).toFixed(1)} MB). Maksimum er 100 MB.`);
        continue;
      }
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const createdAt = new Date();
        next.push({
          id: crypto.randomUUID(),
          identifier: newAttachmentIdentifier(),
          kind: 'file',
          title: attachmentTitleFromFileName(file.name),
          contentType: file.type,
          data: bytesToBase64(bytes),
          creation: attachmentCreationTime({
            lastModified: file.lastModified,
            createdAt,
          }),
        });
      } catch {
        toast.error(`Filen "${file.name}" kunne ikke læses.`);
      }
    }
    if (next.length) setAttachments((prev) => [...prev, ...next]);
  };

  const handleAddUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    setAttachments((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        identifier: newAttachmentIdentifier(),
        kind: 'url',
        url: trimmed,
        title: trimmed,
      },
    ]);
    setUrlInput('');
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!connection || !connection.sor_id) {
      setSubmitError('Klinikken er ikke konfigureret til MedCom. Gå til Indstillinger → Communicate.');
      return;
    }

    if (topicRequired(category) && !topic.trim()) {
      setSubmitError('Emne er påkrævet');
      return;
    }

    if (!messageText.trim()) {
      setSubmitError('Beskedtekst er påkrævet');
      return;
    }

    if (forwardChoice.nothingChosen) {
      setSubmitError('Vælg mindst én del af beskeden, der skal videresendes');
      return;
    }

    if (!patientCpr || !patientFamily) {
      setSubmitError('Patient CPR/erstatnings-CPR og efternavn er påkrævet');
      return;
    }

    if (!authorFamily || !authorPhone) {
      setSubmitError('Forfatter efternavn og telefon er påkrævet');
      return;
    }

    if (!receiverSorId || !receiverName) {
      setSubmitError('Modtager organisation (SOR-id og navn) er påkrævet');
      return;
    }

    if (!activeClinicId) {
      setSubmitError('Ingen aktiv klinik valgt');
      return;
    }

    const parentReplyOrLetterId =
      messageType === 'reply'
        ? (replyParentId ?? letterMessageId ?? undefined)
        : (letterMessageId ?? undefined);

    const body: SendCareCommunicationInput = {
      clinicId: activeClinicId,
      type: messageType,
      ...(messageType !== 'new' && threadId ? {
        parent: {
          communicationIdentifier: threadId,
          ...(parentReplyOrLetterId ? { messageId: parentReplyOrLetterId } : {}),
        },
      } : {}),
      ...(messageType === 'reply'
          ? specificRecipientBody({
              label: specificRecipientLabel,
              recipient: specificRecipient,
              careTeam: specificRecipientCareTeam,
            })
          : {}),
      ...(messageType !== 'new' ? replyEncounterPayload(letter) ?? {} : {}),
      ...(messageType === 'forward' && forwardChoice.segmentIds
        ? { forwardSegmentIds: forwardChoice.segmentIds }
        : {}),
      sender: {
        sorId: senderSorId || connection.sor_id,
        name: senderName || activeClinic?.name || connection.sor_id,
        gln: senderGln || connection.ean || undefined,
      },
      receiver: {
        sorId: receiverSorId,
        name: receiverName,
        gln: receiverEan || undefined,
      },
      patient: {
        ...(messageType !== 'new' && hasReplyIdentifier(replyIdentifiers)
          ? replyIdentifiers
          : patientIdentifiersFromField(patientIdKind, patientCpr)),
        family: patientFamily,
        given: patientGiven.trim() ? patientGiven.trim().split(/\s+/).filter(Boolean) : undefined,
      },
      category,
      topic: outgoingTopic(topic),
      priority: showPriority ? priority : undefined,
          author: {
            family: authorFamily,
            given: authorGiven.trim() ? [authorGiven.trim()] : [authorFamily],
            roleCode: authorRoleCode,
            phone: authorPhone,
          },
      payloads: composeOutgoingPayloads({
        text: messageText,
        includeEmptyText: true,
        carried: mayCarryThreadAttachments(messageType)
          ? carriedAttachmentPayloads(
              threadAttachments,
              selectedThreadAttachments,
              forwardedIds,
            )
          : [],
        newAttachments: attachments.map((a) =>
          a.kind === 'url'
            ? {
                identifier: a.identifier,
                contentType: 'text/plain',
                title: a.title,
                url: a.url,
              }
            : {
                identifier: a.identifier,
                contentType: a.contentType ?? 'application/octet-stream',
                title: a.title,
                data: a.data,
                creation: a.creation,
              },
        ),
      }),
    };

    const citedLabIds = labReportId ? [labReportId] : [];
    if (citedLabIds.length > 0) {
      body.labReportIds = citedLabIds;
    }

    setSubmitting(true);
    try {
      if (messageType === 'forward' || citedLabIds.length > 0) {
        const allowed = await labShareCheck(activeClinicId, {
          messageId: parentReplyOrLetterId,
          labReportIds: citedLabIds,
        });
        if (!allowed) {
          setSubmitError(t('patients.lab.forward_blocked'));
          return;
        }
      }
      const result = await send.mutateAsync(body);
      toast.success('Besked sendt succesfuldt!');
      console.log('Besked sendt:', result);
      
      // Clear form and URL parameters
      setMessageText('');
      setTopic('');
      setReceiverSorId('');
      setReceiverName('');
      setReceiverEan('');
      setAttachments([]);
      setSelectedThreadAttachments(new Set());
      setUrlInput('');
      setSearchParams({});
    } catch (err) {
      console.error('Send error details:', err);
      const msg = err instanceof ApiCallError ? err.message : String(err);
      setSubmitError(msg);
      toast.error(`Afsendelse fejlede: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!connection) {
    return (
      <Card>
        <div className="flex flex-col gap-s3" aria-busy>
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </Card>
    );
  }

  if (!connection.sor_id || !connection.ean) {
    return (
      <ErrorAlert message="MedCom Ikke Konfigureret: Denne klinik er ikke blevet konfigureret til MedCom kommunikation. Gå til Indstillinger → Communicate for at opsætte SOR-id og EAN." />
    );
  }

  return (
    <Card>
    <form onSubmit={handleSubmit} className="space-y-s5">
      <h2 className={cn(TYPE.heading, 'text-ds-text')}>
        {messageType === 'reply' ? t('communicate.compose_reply_title') :
         messageType === 'forward' ? t('communicate.compose_forward_title') :
         t('communicate.compose_new_title')}
      </h2>

      {messageType !== 'new' && threadId && (
        <div className="bg-ds-teal-tint border border-ds-teal-line rounded-lg p-4 mb-4">
          <p className="text-sm text-ds-teal">
            {messageType === 'reply' ? 'Svar' : 'Videresend'}
          </p>
        </div>
      )}

      {submitError && <ErrorAlert message={submitError} />}

      {/* Sender organisation: clinic by default; extra SOR only when authorised */}
      <div className="bg-ds-sunk p-4 rounded-md space-y-3">
        <h3 className="font-medium text-sm text-ds-text">
          {t('communicate.field_sender_choice')}
        </h3>
        {canChooseSender && (
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1" htmlFor="medcom-sender-choice">
              {t('communicate.field_sender_choice')}
            </label>
            <select
              id="medcom-sender-choice"
              value={senderSorId}
              onChange={(e) => {
                senderPicked.current = true;
                setSenderSorId(e.target.value);
              }}
              className="w-full px-3 py-2 border border-ds-field rounded-md"
            >
              {senderChoices.map((choice) => (
                <option key={choice.sorId} value={choice.sorId}>
                  {[
                    choice.name,
                    choice.sorId ? `SOR ${choice.sorId}` : '',
                    choice.gln ? `EAN ${choice.gln}` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              {t('communicate.field_sender_sor')}
            </label>
            <Input type="text" value={senderSorId} readOnly className="bg-ds-sunk font-mono" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              {t('communicate.field_sender_name')}
            </label>
            <Input type="text" value={senderName} readOnly className="bg-ds-sunk" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              {t('communicate.field_sender_ean')}
            </label>
            <Input type="text" value={senderGln} readOnly className="bg-ds-sunk font-mono" />
          </div>
        </div>
      </div>

      {/* Message Category & Topic */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-ds-text mb-1">
            Kategori <span className="text-ds-red-ink">*</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as CareCommunicationCategory)}
            className="w-full px-3 py-2 border border-ds-field rounded-md"
            required
          >
            {[...CARE_COMMUNICATION_CATEGORIES]
              .sort((category, otherCategory) => {
                const label = t(`communicate.category_${category}`);
                const other = t(`communicate.category_${otherCategory}`);
                return label.localeCompare(other, 'da');
              })
              .map((cat) => (
                <option key={cat} value={cat}>
                  {t(`communicate.category_${cat}`)}
                </option>
              ))}
          </select>
        </div>

        {showPriority && (
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              Prioritet
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as 'routine' | 'asap')}
              className="w-full px-3 py-2 border border-ds-field rounded-md"
            >
              <option value="routine">Rutine</option>
              <option value="asap">Hurtigst Muligt</option>
            </select>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-ds-text mb-1">
          Emne{topicRequired(category) ? <> <span className="text-ds-red-ink">*</span></> : null}
        </label>
        <Input
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Besked emne"
          maxLength={500}
          required={topicRequired(category)}
        />
      </div>

      {/* Receiver */}
      <div className="border-t pt-4">
        <h3 className="font-medium text-sm text-ds-text mb-3">Modtager Organisation</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              SOR-id <span className="text-ds-red-ink">*</span>
            </label>
            <Input
              type="text"
              value={receiverSorId}
              onChange={(e) => setReceiverSorId(e.target.value)}
              placeholder="f.eks. 441211000016000"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              Organisations Navn <span className="text-ds-red-ink">*</span>
            </label>
            <Input
              type="text"
              value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)}
              placeholder="f.eks. Aarhus Universitetshospital"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              EAN (GLN)
            </label>
            <Input
              type="text"
              value={receiverEan}
              onChange={(e) => setReceiverEan(e.target.value)}
              placeholder="f.eks. 5790001987244"
            />
          </div>
        </div>
        {messageType === 'reply' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-ds-text mb-1" htmlFor="medcom-specific-recipient">
              Specifik modtager
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="medcom-specific-recipient"
                type="text"
                value={specificRecipientLabel}
                onChange={(e) => {
                  const value = e.target.value;
                  setSpecificRecipientLabel(value);
                  if (!value.trim()) {
                    setSpecificRecipient(null);
                    setSpecificRecipientCareTeam(null);
                  }
                }}
                placeholder="Ingen specifik modtager"
              />
              {specificRecipientLabel.trim().length > 0 && (
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ds-muted hover:bg-ds-row-hover hover:text-ds-text"
                  aria-label="Fjern specifik modtager"
                  onClick={() => {
                    setSpecificRecipientLabel('');
                    setSpecificRecipient(null);
                    setSpecificRecipientCareTeam(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {messageType !== 'new' && (letter?.episodeOfCareIdentifiers.length ?? 0) > 0 && (
        <div className="border-t pt-4">
          <h3 className="font-medium text-sm text-ds-text mb-3">Forløbs-ID</h3>
          <div className="grid grid-cols-2 gap-4">
            {letter?.episodeOfCareIdentifiers.map((episode) => (
              <div key={`${episode.system ?? ''}:${episode.rawValue}`}>
                <label className="block text-sm font-medium text-ds-text mb-1">
                  {episodeOfCareLabel(episode.source)}
                </label>
                <Input
                  type="text"
                  value={episode.value}
                  readOnly
                  className="bg-ds-sunk font-mono"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Patient */}
      <div className="border-t pt-4">
        <h3 className="font-medium text-sm text-ds-text mb-3">Patient</h3>
        <div className="grid grid-cols-4 gap-4">
          {messageType !== 'new' && listedPatientIdentifiers(replyIdentifiers).length > 0 ? (
            listedPatientIdentifiers(replyIdentifiers).map((identifier) => (
              <div key={identifier.kind}>
                <label className="block text-sm font-medium text-ds-text mb-1">
                  {patientIdentifierLabel(identifier.kind)}
                </label>
                <Input type="text" value={identifier.value} readOnly className="bg-ds-sunk font-mono" />
              </div>
            ))
          ) : (
            <>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              Identifikationstype <span className="text-ds-red-ink">*</span>
            </label>
            <select
              value={patientIdKind}
              onChange={(e) => setPatientIdKind(e.target.value as 'cpr' | 'xEcpr' | 'dEcpr')}
              className="w-full px-3 py-2 border border-ds-field rounded-md"
            >
              <option value="cpr">CPR-nummer</option>
              <option value="xEcpr">Erstatnings-CPR (X)</option>
              <option value="dEcpr">Erstatnings-CPR (D)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              {patientIdentifierLabel(patientIdKind)}
              {' '}<span className="text-ds-red-ink">*</span>
            </label>
            <Input
              type="text"
              value={patientCpr}
              onChange={(e) => setPatientCpr(e.target.value)}
              placeholder={patientIdKind === 'cpr' ? 'DDMMÅÅXXXX' : 'Erstatnings-ID'}
              maxLength={patientIdKind === 'cpr' ? 10 : 64}
              required
            />
          </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              Efternavn <span className="text-ds-red-ink">*</span>
            </label>
            <Input
              type="text"
              value={patientFamily}
              onChange={(e) => setPatientFamily(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              Fornavn
            </label>
            <Input
              type="text"
              value={patientGiven}
              onChange={(e) => setPatientGiven(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Author Signature */}
      <div className="border-t pt-4">
        <h3 className="font-medium text-sm text-ds-text mb-3">{t('communicate.section_author')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-ds-text mb-1" htmlFor="medcom-author-choice">
              {t('communicate.field_author_choice')}
            </label>
            <select
              id="medcom-author-choice"
              value={authorUserId}
              onChange={(e) => {
                const next = e.target.value;
                setAuthorUserId(next);
                appliedAuthorUserId.current = next === AUTHOR_FREE_TEXT ? AUTHOR_FREE_TEXT : '';
              }}
              className="w-full px-3 py-2 border border-ds-field rounded-md"
            >
              {authorOptions.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name?.trim() || user.email}
                </option>
              ))}
              <option value={AUTHOR_FREE_TEXT}>{t('communicate.author_free_text')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              {t('communicate.field_author_family')} <span className="text-ds-red-ink">*</span>
            </label>
            <Input
              type="text"
              value={authorFamily}
              onChange={(e) => setAuthorFamily(e.target.value)}
              readOnly={authorLocked}
              className={authorLocked ? 'bg-ds-sunk' : undefined}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              {t('communicate.field_author_given')}
            </label>
            <Input
              type="text"
              value={authorGiven}
              onChange={(e) => setAuthorGiven(e.target.value)}
              readOnly={authorLocked}
              className={authorLocked ? 'bg-ds-sunk' : undefined}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              {t('communicate.field_author_role')} <span className="text-ds-red-ink">*</span>
            </label>
            <select
              value={authorRoleCode}
              onChange={(e) => setAuthorRoleCode(e.target.value)}
              className="w-full px-3 py-2 border border-ds-field rounded-md"
              required
            >
              {AUTHOR_ROLE_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(`communicate.role_${code}`, { defaultValue: code })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text mb-1">
              {t('communicate.field_author_phone')} <span className="text-ds-red-ink">*</span>
            </label>
            <Input
              type="tel"
              value={authorPhone}
              onChange={(e) => setAuthorPhone(e.target.value)}
              placeholder="+4512345678"
              required
            />
          </div>
        </div>
      </div>

      {/* Message Text */}
      <div className="border-t pt-4 space-y-3">
        {(messageType === 'reply' || messageType === 'forward') && (
          <ForwardPriorMessage
            letter={letter}
            threadSegments={threadData?.assembled?.segments}
            choice={messageType === 'forward' ? forwardChoice : undefined}
          />
        )}
        <label className="block text-sm font-medium text-ds-text mb-1">
          Beskedtekst <span className="text-ds-red-ink">*</span>
        </label>
        <textarea
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-ds-field rounded-md"
          placeholder="Indtast din besked her..."
          required
        />
      </div>

      {/* Vedhæftninger */}
      <div className="border-t pt-4">
        <h3 className="font-medium text-sm text-ds-text mb-3">Vedhæftninger</h3>
        <p className="text-xs text-ds-muted mb-3">PDF, GIF, JPEG, PNG, TIFF og BMP. Du kan også tilføje en URL-reference.</p>

        {mayCarryThreadAttachments(messageType) && (
          <div className="mb-3">
            <ThreadAttachmentPicker
              attachments={pickerAttachments}
              selected={selectedThreadAttachments}
              onToggle={(key) => {
                setSelectedThreadAttachments((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              }}
            />
          </div>
        )}

        {/* File picker */}
        <div className="flex gap-3 mb-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_MIME_TYPES}
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
          <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()}>
            <Paperclip />
            Tilføj fil
          </Button>
        </div>

        {/* URL input */}
        <div className="flex gap-2 mb-3">
          <Input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://eksempel.dk/dokument"
            className="flex-1"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUrl(); } }}
          />
          <Button type="button" variant="secondary" onClick={handleAddUrl}>
            <Link />
            Tilføj URL
          </Button>
        </div>

        {/* Attachment list */}
        {attachments.length > 0 && (
          <ul className="space-y-2">
            {attachments.map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-2 bg-ds-sunk border border-ds-line rounded-md px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    {a.kind === 'url' ? <Link className="w-4 h-4 text-ds-teal" /> : <Paperclip className="w-4 h-4 text-ds-muted" />}
                    <span className="font-medium">{a.title}</span>
                    {a.contentType && <span className="text-ds-muted text-xs">({a.contentType})</span>}
                  </span>
                  <span className="mt-0.5 block font-mono text-xs text-ds-muted">
                    {t('communicate.field_attachment_identifier')}: {a.identifier}
                  </span>
                  {a.kind === 'file' && a.creation ? (
                    <span className="mt-0.5 block text-xs text-ds-muted">
                      {t('communicate.field_attachment_creation')}: {a.creation}
                    </span>
                  ) : null}
                  {a.kind === 'file' && (authorGiven.trim() || authorFamily.trim() || authorPhone.trim()) ? (
                    <span className="mt-0.5 block text-xs text-ds-muted">
                      {t('communicate.field_attachment_author')}:{' '}
                      {[authorGiven.trim(), authorFamily.trim()].filter(Boolean).join(' ')}
                      {authorPhone.trim() ? ` · ${authorPhone.trim()}` : ''}
                    </span>
                  ) : null}
                </span>
                <Button
                  type="button"
                  variant="danger"
                  size="icon"
                  onClick={() => removeAttachment(a.id)}
                  aria-label="Slet"
                >
                  <X />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Submit */}
      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="submit"
          disabled={submitting}
          className="flex items-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('communicate.sending')}
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {t('communicate.send_message')}
            </>
          )}
        </Button>
      </div>
    </form>
    </Card>
  );
}

function isCategory(code: string): code is CareCommunicationCategory {
  return (CARE_COMMUNICATION_CATEGORIES as readonly string[]).includes(code);
}

function patientIdentifierLabel(kind: 'cpr' | 'xEcpr' | 'dEcpr'): string {
  switch (kind) {
    case 'cpr':
      return 'CPR-nummer';
    case 'xEcpr':
      return 'Erstatnings-CPR (X)';
    case 'dEcpr':
      return 'Erstatnings-CPR (D)';
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
}

function hasReplyIdentifier(identifiers: ReplyPatientIdentifiers): boolean {
  return Boolean(identifiers.cpr || identifiers.xEcpr || identifiers.dEcpr);
}

function segmentSender(
  assembled: AssembledThread,
): { sorId: string; name: string; ean: string } {
  const sender = assembled.segments[0]?.sender;
  return {
    sorId: sender?.sorId ?? '',
    name: sender?.organizationName ?? '',
    ean: sender?.gln ?? '',
  };
}

function glnForSor(assembled: AssembledThread, sorId: string): string {
  if (!sorId) return '';
  for (const segment of assembled.segments) {
    if (segment.sender.sorId === sorId && segment.sender.gln) return segment.sender.gln;
    if (segment.recipient.sorId === sorId && segment.recipient.gln) return segment.recipient.gln;
  }
  return '';
}

function newestMessageId(
  messages: CareCommunicationListItem[],
  preferInbound: boolean,
): string | null {
  const inbound = messages.filter((message) => message.direction === 'in');
  const pool = preferInbound && inbound.length > 0 ? inbound : messages;
  const sorted = [...pool].sort((a, b) => {
    const left = a.receivedAt ?? a.createdAt;
    const right = b.receivedAt ?? b.createdAt;
    return right.localeCompare(left);
  });
  return sorted[0]?.id ?? null;
}
